import { RETAILER_LABELS, type Retailer } from '@nebueye/shared'
import { useState } from 'react'
import { cn } from '@/lib/utils'

/** Retailer monogram tiles — recognisable at a glance, no trademarked logos. */
const MARK: Record<Retailer, { short: string; className: string }> = {
  target: { short: 'T', className: 'bg-[oklch(0.94_0.04_25)] text-[oklch(0.5_0.19_25)] dark:bg-[oklch(0.3_0.08_25)] dark:text-[oklch(0.85_0.09_25)]' },
  walmart: { short: 'W', className: 'bg-[oklch(0.94_0.04_250)] text-[oklch(0.48_0.15_250)] dark:bg-[oklch(0.3_0.07_250)] dark:text-[oklch(0.85_0.08_250)]' },
  bestbuy: { short: 'BB', className: 'bg-[oklch(0.95_0.07_100)] text-[oklch(0.42_0.1_95)] dark:bg-[oklch(0.32_0.06_100)] dark:text-[oklch(0.88_0.1_100)]' },
  nike: { short: 'N', className: 'bg-[oklch(0.93_0.005_286)] text-[oklch(0.25_0.01_286)] dark:bg-[oklch(0.3_0.01_286)] dark:text-[oklch(0.92_0.01_286)]' },
  pokemoncenter: { short: 'PC', className: 'bg-[oklch(0.95_0.06_90)] text-[oklch(0.45_0.11_75)] dark:bg-[oklch(0.32_0.06_85)] dark:text-[oklch(0.88_0.1_90)]' },
  adidas: { short: 'A', className: 'bg-[oklch(0.93_0.005_286)] text-[oklch(0.25_0.01_286)] dark:bg-[oklch(0.3_0.01_286)] dark:text-[oklch(0.92_0.01_286)]' },
}

export function RetailerMark({ retailer, className }: { retailer: Retailer; className?: string }) {
  const m = MARK[retailer]
  return (
    <span
      title={RETAILER_LABELS[retailer]}
      className={cn('inline-grid size-7 shrink-0 place-items-center rounded-md text-[10px] font-bold tracking-tight', m.className, className)}
    >
      {m.short}
    </span>
  )
}

export function RetailerName({ retailer }: { retailer: Retailer }) {
  return (
    <span className="inline-flex items-center gap-2">
      <RetailerMark retailer={retailer} className="size-5 rounded text-[8px]" />
      {RETAILER_LABELS[retailer]}
    </span>
  )
}

/**
 * A product picture, falling back to the retailer's mark when there is no
 * image or it fails to load, so a list never shows a broken-image icon.
 * `className` sets the size and radius for both.
 */
export function ProductThumb({ src, retailer, className, eager }: { src?: string; retailer: Retailer; className?: string; eager?: boolean }) {
  // Remember which URL failed, so a row reused for another product retries.
  const [failedSrc, setFailedSrc] = useState<string>()
  if (!src || failedSrc === src) return <RetailerMark retailer={retailer} className={className} />
  return (
    <img
      src={src}
      alt=""
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      onError={() => setFailedSrc(src)}
      className={cn('bg-surface-sunken ring-border shrink-0 object-cover ring-1', className)}
    />
  )
}
